export interface OpenGraphImageResizeParams {
  src: MaybeRef<string | undefined>
}

// https://iamturns.com/open-graph-image-size/
export function useOpenGraphImageResize(params: OpenGraphImageResizeParams) {
  const runtimeConfig = useRuntimeConfig();
  const src = toRef(params.src);

  return computed(() => {
    if (!src.value) return ``;
    const url = parseUrl(src.value);
    if (!url) return params.src;
    url.hostname = runtimeConfig.public.img.resizeHostname;
    url.searchParams.set('format', 'auto');
    url.searchParams.set('width', `1200`);
    url.searchParams.set('height', `630`);
    return url.href;
  });
}

export interface ImageResizeParams {
  width: number
  src: string
  resizeHostname: string
  factors?: number
}

export function getImageResize(params: ImageResizeParams) {
  const url = parseUrl(params.src);
  if (!url) return { src: params.src };
  url.hostname = params.resizeHostname;
  url.searchParams.set('format', 'auto');
  url.searchParams.set('width', String(params.width));
  const resizedSrc = url.href;
  const urls = Array
    .from({ length: params.factors ?? 3 })
    .map((_, factor) => {
      const nonZeroBasedFactor = factor + 1;
      const width = params.width * nonZeroBasedFactor;
      url.searchParams.set('width', String(width));
      return `${url.href} ${width}w`;
    });
  url.searchParams.set('width', String(params.width));
  return {
    src: resizedSrc,
    srcset: urls.join(', '),
  };
}

export interface UseImageResizeParams {
  width: MaybeRef<number>
  src: MaybeRef<string>
  factors?: number
}

export function useImageResize(params: UseImageResizeParams) {
  const runtimeConfig = useRuntimeConfig();
  const src = toRef(params.src);
  const width = toRef(params.width);

  return computed(() => getImageResize({
    width: width.value,
    src: src.value,
    resizeHostname: runtimeConfig.public.img.resizeHostname,
    factors: params.factors,
  }));
}

// As of march 2025, URL.parse wasn't widely supported in browsers (±80%)
// https://developer.mozilla.org/en-US/docs/Web/API/URL/URL
function parseUrl(href: string) {
  try {
    const url = new URL(href);
    return url;
  }
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  catch (error) {
    return null;
  }
}
